/**
 * Endpoint URL builders for the YTG API client.
 *
 * Paths are **relative** — the client's ``baseURL`` (set to API_ENDPOINTS.YTG)
 * prepends the service prefix. Keeping paths relative lets us swap the base
 * URL (dev proxy / docker / prod) without touching the endpoint table.
 */

type Id = string | number;

/**
 * Table of relative URL builders for every YTG API route.
 *
 * Each entry returns the path (optionally interpolating ids/params) to pass to
 * the ``ytgApi`` axios client or, for downloads, to YtgService URL helpers.
 */
export const ytgEndpoints = {
    /** List of clients. */
    client:        (): string => `/client`,
    /** List of supported languages. */
    lang:          (): string => `/lang`,

    /** Operations collection (list / create). */
    operation:     (): string => `/operation`,
    /** Single operation by id. */
    operationById: (id: Id): string => `/operation/${id}`,

    /** Paginated rows of an operation. */
    operationRows: (opId: Id): string => `/row/operation/${opId}`,
    /** KPI stats of an operation. */
    operationStats:(opId: Id): string => `/operation/${opId}/stats`,

    /** Rows collection. */
    row:           (): string => `/row`,
    /** Single row by id. */
    rowById:       (id: Id): string => `/row/${id}`,
    /** SERP results for a row. */
    serpByRowId:   (rowId: Id): string => `/serp/${rowId}`,
    /** Top-ranking domains across an operation's SERPs. */
    topDomains:    (opId: Id): string => `/serp/operation/${opId}/top-domains`,

    // fichiers
    /** PDF file for a row. */
    rowPdf: (rowId: Id): string => `/row/${rowId}/pdf`,
    /** ZIP archive of all PDFs of an operation. */
    opZip:  (opId: Id): string  => `/operation/${opId}/pdf_zip`,

    /** Inline HTML preview of a row's guide. */
    rowView:       (rowId: Id): string => `/row/${rowId}/view`,
    /** Ad-hoc content scoring for a row. */
    rowAdhocScore: (rowId: Id): string => `/row/${rowId}/adhoc-score`,
    /** Change the competitor `top` and recompute the stored averages (local, no API). */
    recomputeCompetitors: (opId: Id): string => `/operation/${opId}/recompute-competitors`,

    // ========== PDF GENERATION ==========
    /** Start PDF generation for an operation. */
    generatePdfsStart:  (opId: Id): string => `/operation/${opId}/generate_pdfs/start`,
    /** Poll PDF generation status of an operation. */
    generatePdfsStatus: (opId: Id): string => `/operation/${opId}/generate_pdfs/status`,
    /** Aggregate PDF status overview of an operation. */
    pdfOverview:        (opId: Id): string => `/operation/${opId}/pdf_overview`,

    // ========== GOOGLE DRIVE (PDFs) ==========
    /** Start uploading an operation's PDFs to Google Drive. */
    uploadDriveStart:  (opId: Id): string => `/operation/${opId}/upload_drive/start`,
    /** Poll a Drive PDF upload task by its id. */
    uploadDriveStatus: (opId: Id, taskId: string): string => `/operation/${opId}/upload_drive/status?task_id=${taskId}`,

    // ========== GOOGLE DRIVE (Graphs) ==========
    /** Start uploading an operation's graphs to Google Drive. */
    uploadGraphsStart:  (opId: Id): string => `/operation/${opId}/upload_graphs/start`,
    /** Poll a Drive graph upload task by its id. */
    uploadGraphsStatus: (opId: Id, taskId: string): string => `/operation/${opId}/upload_graphs/status?task_id=${taskId}`,

    // ========== ADMIN OPERATIONS ==========
    /** Admin: stop a running operation. */
    adminStop:              (opId: Id): string => `/admin/operation/${opId}/stop`,
    /** Admin: reset a single task's status on an operation. */
    adminResetStatus:       (opId: Id, task: string): string => `/admin/operation/${opId}/reset-status/${task}`,
    /** Admin: override an operation's overall status (any ProcessStatus value). */
    adminSetOperationStatus:(opId: Id): string => `/admin/operation/${opId}/set-status`,
    /** Admin: override a single task's status (any ProcessStatus value). */
    adminSetTaskStatus:     (opId: Id, task: string): string => `/admin/operation/${opId}/set-status/${task}`,
    /** Admin: restart a task for the whole operation. */
    adminRestart:           (opId: Id, task: string): string => `/admin/operation/${opId}/restart/${task}`,
    /** Admin: restart a task only on rows that failed. */
    adminRestartFailedRows: (opId: Id, task: string): string => `/admin/operation/${opId}/restart/${task}/failed-rows`,
    /** Admin: restart a task on an explicit set of row ids. */
    adminRestartByRowIds:   (opId: Id, task: string): string => `/admin/operation/${opId}/restart/${task}/by-row-ids`,
    /** Admin: per-row task status grid of an operation. */
    adminRowsStatus:        (opId: Id): string => `/admin/operation/${opId}/rows-status`,
    /** Admin: per-row website-extraction trace + outcome summary of an operation. */
    adminOperationExtraction:(opId: Id): string => `/admin/operation/${opId}/extraction`,
    /** Admin: count of failed rows in an operation. */
    adminFailedRowsCount:   (opId: Id): string => `/admin/operation/${opId}/failed-rows-count`,
    /** Admin: error details of an operation. */
    adminErrors:            (opId: Id): string => `/admin/operation/${opId}/errors`,
    /** Admin: delete an operation. */
    adminDelete:            (opId: Id): string => `/admin/operation/${opId}/delete`,

    // ========== ADMIN PDF/DRIVE OPERATIONS ==========
    /** Admin: Drive upload status of an operation. */
    adminDriveStatus:          (opId: Id): string => `/admin/operation/${opId}/drive-status`,
    /** Admin: trigger PDF generation for an operation. */
    adminTriggerPdfGeneration: (opId: Id): string => `/admin/operation/${opId}/trigger-pdf-generation`,
    /** Admin: reset PDF status; optionally mark it as completed. */
    adminResetPdfStatus:       (opId: Id, markCompleted: boolean = false): string => `/admin/operation/${opId}/reset-pdf-status?mark_completed=${markCompleted}`,
    /** Admin: trigger the PDF upload to Drive. */
    adminTriggerPdfUpload:     (opId: Id): string => `/admin/operation/${opId}/trigger-pdf-upload`,
    /** Admin: trigger the graph upload to Drive. */
    adminTriggerGraphUpload:   (opId: Id): string => `/admin/operation/${opId}/trigger-graph-upload`,
    /** Admin: reset the PDF/graph upload flags of an operation. */
    adminResetUploadFlags:     (opId: Id): string => `/admin/operation/${opId}/reset-upload-flags`,
    /** Admin: status of a Celery task by its id. */
    adminTaskStatus:           (taskId: string): string => `/admin/operation/task/${taskId}/status`,
    /** Admin: rename an operation. */
    adminRename:               (opId: Id): string => `/admin/operation/${opId}/rename`,
};
